import React, { useEffect, useState } from "react";

const BulkGenerationLoading = ({ progress, totalEmails = 0, currentEmail = 0 }) => {
  // Berechne den Fortschritt basierend auf den ersten 5 E-Mails
  // Verwende direkt den übergebenen progress-Wert
  const adjustedProgress = Math.min(100, progress || 0);
  
  // Berechne die geschätzte verbleibende Zeit basierend auf dem Fortschritt
  const estimatedTimeRemaining = Math.max(0, 5 - currentEmail) * 20; // ca. 20 Sekunden pro E-Mail
  
  // Formatiere die verbleibende Zeit
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds} seconds`;
    return `${Math.floor(seconds / 60)} min ${seconds % 60} sec`;
  };

  // Füge einen Effekt hinzu, um das Scrollen zu blockieren, wenn die Komponente aktiv ist
  useEffect(() => {
    // Speichere den ursprünglichen overflow-Wert
    const originalOverflow = document.body.style.overflow;
    const originalEmailPopupContainerOverflow = document.querySelector('.emailAiPopupContainer')?.style.overflow;
    const originalEmailPopupContentOverflow = document.querySelector('.emailAiPopupContent')?.style.overflow;
    
    // Verhindere Scrollen
    document.body.style.overflow = 'hidden';
    
    // Blockiere auch das Scrollen in den spezifischen Containern
    if (document.querySelector('.emailAiPopupContainer')) {
      document.querySelector('.emailAiPopupContainer').style.overflow = 'hidden';
    }
    
    if (document.querySelector('.emailAiPopupContent')) {
      document.querySelector('.emailAiPopupContent').style.overflow = 'hidden';
    }
    
    // Füge eine Klasse zum Body hinzu, um weitere CSS-Regeln anzuwenden
    document.body.classList.add('bulk-generation-active');
    
    // Entferne die Beschränkungen beim Unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      
      if (document.querySelector('.emailAiPopupContainer')) {
        document.querySelector('.emailAiPopupContainer').style.overflow = originalEmailPopupContainerOverflow;
      }
      
      if (document.querySelector('.emailAiPopupContent')) {
        document.querySelector('.emailAiPopupContent').style.overflow = originalEmailPopupContentOverflow;
      }
      
      document.body.classList.remove('bulk-generation-active');
    };
  }, []);

  return (
    <div className="bulk-generation-overlay" style={{
      position: 'fixed', // Ändere auf fixed, um das ganze Fenster zu blockieren
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999, // Erhöhter z-index, um sicherzustellen, dass es über allem liegt
      overflowY: 'hidden' // Verhindere internes Scrollen
    }}>
      {/* Floating particles for visual effect */}
      <div className="bulk-particle"></div>
      <div className="bulk-particle"></div>
      <div className="bulk-particle"></div>
      
      <div className="bulk-generation-content">
        <div className="bulk-generation-logo-container">
          <img 
            src="/img/salesgod_dark.webp" 
            alt="SalesGod Logo" 
            className="bulk-generation-logo" 
            style={{
              width: "80px",
              height: "80px",
              marginBottom: "20px",
              animation: "spin 3s linear infinite",
              filter: "drop-shadow(0 0 10px rgba(255, 255, 255, 0.8))"
            }}
          />
        </div>
        
        <h2 className="bulk-generation-title">Generating Emails</h2>
        
        <div className="bulk-generation-description" style={{
          fontSize: "16px",
          marginBottom: "30px",
          textAlign: "center",
          maxWidth: "450px",
          lineHeight: "1.5"
        }}>
          {adjustedProgress < 100 
            ? `Creating personalized emails based on your data. Processing ${totalEmails} emails in total.`
            : `The first 5 emails are ready for your review! The remaining ${Math.max(0, totalEmails - 5)} emails will continue generating in the background.`}
        </div>
        
        <div className="bulk-generation-stats" style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "15px",
          marginBottom: "20px"
        }}>
          <div className="bulk-generation-stat" style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "12px 15px",
            background: "rgba(255, 255, 255, 0.07)",
            borderRadius: "8px",
            backdropFilter: "blur(5px)"
          }}>
            <span className="stat-label" style={{ color: "var(--blue) !important", fontWeight: "500" }}>Progress:</span>
            <span className="stat-value" style={{ color: "var(--blue) !important", fontWeight: "600" }}>{Math.round(adjustedProgress)}%</span>
          </div>
          
          <div className="bulk-generation-stat" style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "12px 15px",
            background: "rgba(255, 255, 255, 0.07)",
            borderRadius: "8px",
            backdropFilter: "blur(5px)"
          }}>
            <span className="stat-label" style={{ color: "var(--blue) !important", fontWeight: "500" }}>Created:</span>
            <span className="stat-value" style={{ color: "var(--blue) !important", fontWeight: "600" }}>{currentEmail} of {totalEmails} emails</span>
          </div>
          
          <div className="bulk-generation-stat" style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "12px 15px",
            background: "rgba(255, 255, 255, 0.07)",
            borderRadius: "8px",
            backdropFilter: "blur(5px)"
          }}>
            <span className="stat-label" style={{ color: "var(--blue) !important", fontWeight: "500" }}>Estimated time remaining:</span>
            <span className="stat-value" style={{ color: "var(--blue) !important", fontWeight: "600" }}>{formatTime(estimatedTimeRemaining)}</span>
          </div>
        </div>
        
        <div className="bulk-generation-progress-container" style={{ width: "100%", marginBottom: "25px" }}>
          <div className="bulk-generation-progress-bar-container">
            <div 
              className="bulk-generation-progress-bar" 
              style={{ width: `${adjustedProgress}%` }}
            ></div>
          </div>
        </div>
        
        <p className="bulk-generation-note" style={{
          fontSize: "14px",
          color: "var(--blue)",
          fontStyle: "italic",
          opacity: "0.9",
          maxWidth: "450px",
          margin: "0 auto",
          textAlign: "center"
        }}>
          {adjustedProgress < 100 ? 
            "Your emails are being generated. Once the first 5 emails are finished, you can start working with them while the rest continues in the background." :
            "The first 5 emails are ready! You can now work with them while the remaining emails are generated in the background."}
        </p>
      </div>
    </div>
  );
};

export default BulkGenerationLoading; 